import { H4 } from "@dgs/core";
import { Chart, ChartConfiguration } from "chart.js";
import { FC, useLayoutEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { EventAnalyticsRatingCountResource } from "./analyticsTypes";
import { ChartColumn } from "./shared";

interface Props {
	counts: EventAnalyticsRatingCountResource[];
}

const Canvas = styled.canvas`
	max-height: 400px;
	width: 100%;
`;

export const CountByRatingChart: FC<Props> = ({ counts }) => {
	const { t } = useTranslation();
	const ref = useRef<HTMLCanvasElement>(null);
	const theme = useTheme();

	const config = useMemo<ChartConfiguration>(() => {
		return {
			type: "bar",
			data: {
				labels: counts.map(({ rating }) => rating),
				datasets: [
					{
						backgroundColor: theme.colors.palette.primary.main.base,
						data: counts.map(({ count, rating }) => ({ x: rating, y: count })),
					},
				],
			},
			options: {
				plugins: {
					tooltip: {
						enabled: false,
					},
				},
				scales: {
					x: {
						title: {
							display: true,
							text: t("Event rating"),
							font: {
								size: theme.fonts.sizes.normal,
							},
						},
						grid: {
							display: false,
						},
					},
					y: {
						title: {
							display: true,
							text: t("Event count"),
							font: {
								size: theme.fonts.sizes.normal,
							},
						},
						ticks: {
							stepSize: 1,
							precision: 0,
						},
					},
				},
			},
		};
	}, [counts, t, theme.colors.palette.primary.main.base, theme.fonts.sizes.normal]);

	useLayoutEffect(() => {
		if (ref.current) {
			const myChart = new Chart(ref.current, config);

			return () => {
				myChart.destroy();
			};
		}
	}, [config]);

	return (
		<ChartColumn $gap={8}>
			<H4>{t("Event rating")}</H4>
			<Canvas ref={ref} />
		</ChartColumn>
	);
};
